import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { CrmTabs } from "@/components/admin/CrmTabs";
import { api, money } from "@/lib/api";
import { inputCls, Field, Btn, Th, Td, TableWrap, EmptyRow } from "@/components/admin/ui";

const statusBadge = {
  open: "border border-white/30 text-neutral-300",
  partial: "border border-yellow-400/50 text-yellow-400",
  paid: "bg-white text-black",
};

export default function CrmInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loads, setLoads] = useState([]);
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ load_id: "", square_link: "" });
  const [payFor, setPayFor] = useState(null);
  const [payment, setPayment] = useState({ amount: "", method: "Square", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (status) params.status = status;
    api.get("/crm/invoices", { params }).then(({ data }) => setInvoices(data)).catch(() => {});
    api.get("/crm/loads").then(({ data }) => setLoads(data)).catch(() => {});
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const createInvoice = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/crm/invoices", { load_id: form.load_id, square_link: form.square_link || null });
      toast.success(`Invoice ${data.invoice_no} created — ${money(data.amount)}`);
      setForm({ load_id: "", square_link: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create invoice");
    } finally {
      setSaving(false);
    }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/crm/invoices/${payFor}/payments`, {
        amount: Number(payment.amount),
        method: payment.method || null,
        note: payment.note || null,
      });
      toast.success(`Payment recorded — balance ${money(data.balance)}`);
      setPayFor(null);
      setPayment({ amount: "", method: "Square", note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
    } finally {
      setSaving(false);
    }
  };

  const invoicedLoadIds = new Set(invoices.map((i) => i.load_id));
  const invoiceableLoads = loads.filter((l) => !invoicedLoadIds.has(l.id));

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="crm-invoices-page">
      <AdminNav />
      <CrmTabs />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <h1 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl">Invoices & Payments</h1>
          <div className="flex flex-wrap gap-2">
            <select data-testid="invoices-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-auto bg-[#0B0B0B]`}>
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
            <Btn data-testid="invoices-add-toggle" onClick={() => setShowForm(!showForm)}><Plus size={13} /> New Invoice</Btn>
          </div>
        </div>

        {showForm && (
          <form onSubmit={createInvoice} className="border border-white/15 bg-[#101010] p-5 sm:p-7 mb-6 grid sm:grid-cols-2 gap-4 items-end" data-testid="invoice-add-form">
            <Field label="Load *" id="i-load">
              <select id="i-load" data-testid="invoice-load-select" required value={form.load_id} onChange={(e) => setForm({ ...form, load_id: e.target.value })} className={`${inputCls} bg-[#101010]`}>
                <option value="">Select load...</option>
                {invoiceableLoads.map((l) => (
                  <option key={l.id} value={l.id}>{l.load_no} — {l.carrier_name} — fee {money(l.fee_amount)}</option>
                ))}
              </select>
            </Field>
            <Field label="Square Payment Link" id="i-square">
              <input id="i-square" data-testid="invoice-square-input" type="url" value={form.square_link} onChange={(e) => setForm({ ...form, square_link: e.target.value })} className={inputCls} placeholder="https://square.link/u/..." />
            </Field>
            <div className="sm:col-span-2">
              <Btn type="submit" data-testid="invoice-save-button" disabled={saving}>{saving ? "Creating..." : "Generate Invoice"}</Btn>
            </div>
          </form>
        )}

        <TableWrap testid="invoices-table">
          <thead>
            <tr className="border-b border-white/10"><Th>Invoice</Th><Th>Carrier</Th><Th>Load</Th><Th>Amount</Th><Th>Paid</Th><Th>Balance</Th><Th>Status</Th><Th>Square</Th><Th></Th></tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <EmptyRow cols={9}>No invoices yet — generate one from a load</EmptyRow>
            ) : (
              invoices.map((inv) => (
                <>
                  <tr key={inv.id} className="border-b border-white/5" data-testid={`invoice-row-${inv.id}`}>
                    <Td className="font-mono text-xs text-neutral-400">{inv.invoice_no}</Td>
                    <Td className="text-neutral-300">{inv.carrier_name}</Td>
                    <Td className="font-mono text-xs text-neutral-400">{inv.load_no}</Td>
                    <Td className="font-mono text-xs">{money(inv.amount)}</Td>
                    <Td className="font-mono text-xs text-neutral-400">{money(inv.paid_total)}</Td>
                    <Td className="font-mono text-xs" data-testid={`invoice-balance-${inv.id}`}>{money(inv.balance)}</Td>
                    <Td>
                      <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 ${statusBadge[inv.status]}`} data-testid={`invoice-status-${inv.id}`}>
                        {inv.status}
                      </span>
                    </Td>
                    <Td>
                      {inv.square_link ? (
                        <a href={inv.square_link} target="_blank" rel="noopener noreferrer" data-testid={`invoice-square-${inv.id}`} className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white underline underline-offset-4">
                          Pay Link
                        </a>
                      ) : <span className="text-neutral-600">—</span>}
                    </Td>
                    <Td>
                      <div className="flex gap-3 whitespace-nowrap">
                        <a href={`/admin/crm/invoices/${inv.id}/print`} data-testid={`invoice-print-${inv.id}`} className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white underline underline-offset-4">
                          Print
                        </a>
                        <button
                          data-testid={`invoice-send-${inv.id}`}
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/crm/invoices/${inv.id}/send`);
                              toast.success(`Invoice emailed to ${data.to}`);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Could not send invoice");
                            }
                          }}
                          className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white underline underline-offset-4"
                        >
                          Send
                        </button>
                        {inv.status !== "paid" && (
                          <button
                            data-testid={`invoice-remind-${inv.id}`}
                            onClick={async () => {
                              try {
                                const { data } = await api.post(`/crm/invoices/${inv.id}/remind`);
                                toast.success(`Reminder emailed to ${data.to}`);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Could not send reminder");
                              }
                            }}
                            className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-500/80 hover:text-yellow-400 underline underline-offset-4"
                          >
                            Remind
                          </button>
                        )}
                        {inv.status !== "paid" && (
                          <button data-testid={`invoice-add-payment-${inv.id}`} onClick={() => setPayFor(payFor === inv.id ? null : inv.id)} className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white underline underline-offset-4">
                            + Payment
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                  {payFor === inv.id && (
                    <tr key={`${inv.id}-pay`} className="border-b border-white/5 bg-white/[0.03]">
                      <Td colSpan={9}>
                        <form onSubmit={addPayment} className="flex flex-wrap gap-3 items-end py-2" data-testid={`payment-form-${inv.id}`}>
                          <Field label="Amount $" id={`p-amount-${inv.id}`}>
                            <input id={`p-amount-${inv.id}`} data-testid={`payment-amount-${inv.id}`} type="number" step="0.01" min="0.01" required value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} className={`${inputCls} w-32`} />
                          </Field>
                          <Field label="Method" id={`p-method-${inv.id}`}>
                            <input id={`p-method-${inv.id}`} data-testid={`payment-method-${inv.id}`} value={payment.method} onChange={(e) => setPayment({ ...payment, method: e.target.value })} className={`${inputCls} w-36`} />
                          </Field>
                          <Field label="Note" id={`p-note-${inv.id}`}>
                            <input id={`p-note-${inv.id}`} data-testid={`payment-note-${inv.id}`} value={payment.note} onChange={(e) => setPayment({ ...payment, note: e.target.value })} className={`${inputCls} w-48`} />
                          </Field>
                          <Btn type="submit" data-testid={`payment-save-${inv.id}`} disabled={saving}>{saving ? "..." : "Record"}</Btn>
                        </form>
                        {inv.payments.length > 0 && (
                          <p className="font-mono text-[10px] text-neutral-500 mt-2">
                            History: {inv.payments.map((p) => `${money(p.amount)} ${p.method || ""}`.trim()).join(" · ")}
                          </p>
                        )}
                      </Td>
                    </tr>
                  )}
                </>
              ))
            )}
          </tbody>
        </TableWrap>
      </main>
    </div>
  );
}
